import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const Founder = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  return (
    <section
      id="founder"
      data-testid="founder-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 border-t border-zinc-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold/60 mb-8" data-testid="founder-label">
            {t.founder.label}
          </p>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] overflow-hidden"
            >
              <img
                src="/founder.png"
                alt={t.founder.name}
                className="w-full h-full object-cover grayscale-image"
              />
              <div className="absolute inset-0 border border-gold/20" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-8"
            >
              <h2 className="text-4xl md:text-6xl tracking-tight leading-none text-zinc-100" data-testid="founder-name">
                {t.founder.name}
              </h2>

              <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                {t.founder.role}
              </p>

              <div className="space-y-4">
                <h3 className="text-xl font-light text-gold" data-testid="founder-competencies-title">
                  {t.founder.competenciesTitle}
                </h3>
                <ul className="space-y-3 text-zinc-400">
                  {t.founder.competencies.map((competency, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-gold mt-1">—</span>
                      <span>{competency}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-light text-gold" data-testid="founder-projects-title">
                  {t.founder.projectsTitle}
                </h3>
                <ul className="space-y-3 text-zinc-400">
                  {t.founder.projects.map((project, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-gold mt-1">—</span>
                      <span>{project}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Founder;
